import { Chain, ChainID } from '@ankr.com/chains-list';
import { t } from '@ankr.com/common';

import { AccountPermission } from 'modules/permissions/constants';
import { Placeholder } from 'modules/common/components/Placeholder';
import { UpgradeToPremiumPlanDialog } from 'modules/common/components/UpgradeToPremiumPlanDialog';
import { useAccount } from 'modules/account/hooks/useAccount';
import { useAccountStatus } from 'modules/accountStatus/hooks/useAccountStatus';
import { useAuth } from 'modules/auth/hooks/useAuth';
import { useCheckAccountPermission } from 'modules/account/hooks/useCheckAccountPermission';
import { useDialog } from 'modules/common/hooks/useDialog';
import {
  EndpointGroup,
  TAC_TESTNETS_GROUPS_LIST,
} from 'modules/endpoints/types';

import { EndpointPlaceholder } from '../EndpointPlaceholder';
import { EndpointsHeader } from '../EndpointsHeader';
import { WSEndpoints } from '../WSEndpoints';
import { renderTacTitle } from './utils/renderTacTitle';

interface IWsFeatureEndpointsProps {
  title: string;
  hasWSFeature: boolean;
  group: EndpointGroup;
  publicChain: Chain;
  wss: string[];
}

export const WsFeatureEndpoints = ({
  group,
  hasWSFeature,
  publicChain,
  title,
  wss,
}: IWsFeatureEndpointsProps) => {
  const hasWSEndpointsAccess = useCheckAccountPermission({
    permission: AccountPermission.ChainItem,
  });

  const {
    isOpened: isPromoDialogOpened,
    onClose: onPromoDialogClose,
    onOpen: onPromoDialogOpen,
  } = useDialog();

  const { isLoggedIn } = useAuth();
  const { isDev } = useAccount();
  const { isPremium } = useAccountStatus();

  const hasNoAccess = isLoggedIn && !hasWSEndpointsAccess;

  if (!hasWSFeature || hasNoAccess) {
    return null;
  }

  const isTacTestnetEndpoints =
    publicChain.id === ChainID.TAC &&
    TAC_TESTNETS_GROUPS_LIST.includes(group.id);

  const header = <EndpointsHeader title={title} />;

  if (isTacTestnetEndpoints && wss.length > 0) {
    return (
      <>
        {wss.map(wssEndpoint => (
          <WSEndpoints
            key={wssEndpoint}
            title={
              <EndpointsHeader title={renderTacTitle(wssEndpoint) || title} />
            }
            wss={[wssEndpoint]}
          />
        ))}
      </>
    );
  }

  return (
    <Placeholder
      hasPlaceholder={isPremium}
      placeholder={<WSEndpoints title={header} wss={wss} />}
    >
      <Placeholder hasPlaceholder={isDev} placeholder={null}>
        <EndpointPlaceholder
          lockedLabel={t('chain-item.get-started.endpoints.lockedLabelWss')}
          onClick={onPromoDialogOpen}
          title={header}
        />
        <UpgradeToPremiumPlanDialog
          isPromoDialogOpened={isPromoDialogOpened}
          onPromoDialogClose={onPromoDialogClose}
        />
      </Placeholder>
    </Placeholder>
  );
};
